// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.

// For future forward-compatibility with regexp `v` flag, `RESERVED_CHARS` is
// autogenerated from the `ClassSetReservedDoublePunctuator`,
// `ClassSetSyntaxCharacter`, and `ClassSetReservedPunctuator` categories in the
// {@link https://github.com/tc39/proposal-regexp-v-flag#how-is-the-v-flag-different-from-the-u-flag | draft spec}.
// ```ts
// const reservedChars = [
//   ...new Set(
//     [
//       "ClassSetReservedDoublePunctuator",
//       "ClassSetSyntaxCharacter",
//       "ClassSetReservedPunctuator",
//     ].map((n) =>
//      document.querySelector(`[name=${n}] emu-rhs`).textContent.replaceAll(
//        /\s/g,
//        "",
//      )
//    ).join(""),
//  ),
// ];
// const RESERVED_CHARS = Object.fromEntries(reservedChars
//   .map((x) => {
//     try {
//       for (const flag of "gimsuy") {
//         new RegExp(`\\${x}`, flag);
//         new RegExp(`[\\${x}]`, flag);
//       }
//       return [x, `\\${x}`];
//     } catch (e) {
//       return [x, `\\x${x.codePointAt(0).toString(16).padStart(2, "0")}`];
//     }
// }));
// ```
const RESERVED_CHARS = {
  "&": "\\x26",
  "!": "\\x21",
  "#": "\\x23",
  $: "\\$",
  "%": "\\x25",
  "*": "\\*",
  "+": "\\+",
  ",": "\\x2c",
  ".": "\\.",
  ":": "\\x3a",
  ";": "\\x3b",
  "<": "\\x3c",
  "=": "\\x3d",
  ">": "\\x3e",
  "?": "\\?",
  "@": "\\x40",
  "^": "\\^",
  "`": "\\x60",
  "~": "\\x7e",
  "(": "\\(",
  ")": "\\)",
  "[": "\\[",
  "]": "\\]",
  "{": "\\{",
  "}": "\\}",
  "/": "\\/",
  "-": "\\x2d",
  "\\": "\\\\",
  "|": "\\|",
} as const;

const RX_REGEXP_ESCAPE = new RegExp(
  `[${Object.values(RESERVED_CHARS).join("")}]`,
  "gu",
);

/**
 * Escapes arbitrary text for interpolation into a regexp, such that it will
 * match exactly that text and nothing else.
 *
 * @example Usage
 * ```ts
 * import { escape } from "@std/regexp/escape";
 * import { assertEquals, assertMatch, assertNotMatch } from "@std/assert";
 *
 * const re = new RegExp(`^${escape(".")}$`, "u");
 *
 * assertEquals("^\\.$", re.source);
 * assertMatch(".", re);
 * assertNotMatch("a", re);
 * ```
 *
 * @param str The string to escape.
 * @returns The escaped string.
 */
export function escape(str: string): string {
  return str.replaceAll(
    RX_REGEXP_ESCAPE,
    (m) => RESERVED_CHARS[m as keyof typeof RESERVED_CHARS],
  );
}

// denoCacheMetadata={"headers":{"date":"Fri, 08 Nov 2024 09:52:34 GMT","x-jsr-cache-id":"IAD","content-length":"2418","x-goog-metageneration":"1","cross-origin-resource-policy":"cross-origin","expires":"Sat, 08 Nov 2025 09:52:34 GMT","x-guploader-uploadid":"AHmUCY0z1RWUBvKIVSDzusQ-FM3-kSjQ8JmHebWoP1rpN2Xm8NO6p_7kbYGmXv1-H73aJLqqOQBa712PMw","last-modified":"Fri, 12 Jul 2024 10:50:23 GMT","x-goog-storage-class":"STANDARD","content-type":"text/typescript","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-goog-stored-content-length":"2418","accept-ranges":"bytes","x-robots-tag":"noindex","access-control-allow-origin":"*","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-jsr-cache-status":"hit","access-control-expose-headers":"*","x-goog-stored-content-encoding":"identity","x-goog-hash":"crc32c=NXjnjw==,md5=mq2vggTNoLWsqs/NDOvFzA==","etag":"\"9aadaf8204cda0b5acaacfcd0cebc5cc\"","server":"UploadServer","x-goog-generation":"1720781423375020","x-content-type-options":"nosniff","age":"1047","cache-control":"public, max-age=31536000, immutable","via":"1.1 google"},"url":"https://jsr.io/@std/regexp/1.0.0/escape.ts","time":1731060600}